/**
 * Stats → Keyword Database (A7Y-4883).
 *
 * One page, five tabs (Overview, Fill, Markets, Sources, Coverage). Every
 * block is its own cached request (`/admin/stats/keyword-database/<block>`) so
 * one slow query never holds the others; Refresh re-fetches the active blocks
 * with `force`, which bypasses the server cache. Tab and fill window live in
 * the URL (`?tab=&days=`).
 */
import { useRef, useState, type MutableRefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { KEYWORD_DATABASE_FILL_DAYS, KEYWORD_DATABASE_TABS } from '@a7y/types';
import type {
  KeywordDatabaseCoverage,
  KeywordDatabaseFill,
  KeywordDatabaseFillDays,
  KeywordDatabaseHeadline,
  KeywordDatabaseMarkets,
  KeywordDatabaseOverview,
  KeywordDatabaseSources,
  KeywordDatabaseTab,
} from '@a7y/types';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { BlockPanel } from './components/BlockPanel';
import { CoverageTab } from './components/CoverageTab';
import { FillTab } from './components/FillTab';
import { HeadlineStrip } from './components/HeadlineStrip';
import { MarketsTab } from './components/MarketsTab';
import { NoCountedRows } from './components/NoCountedRows';
import { OverviewTab } from './components/OverviewTab';
import { SourcesTab } from './components/SourcesTab';

const TAB_TRIGGER_CLASSES =
  'px-3 py-1.5 text-sm rounded-md text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100 data-[state=active]:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400';

const TAB_LABEL: Record<KeywordDatabaseTab, string> = {
  overview: 'Overview',
  fill: 'Fill',
  markets: 'Markets',
  sources: 'Sources',
  coverage: 'Coverage',
};

const DEFAULT_TAB: KeywordDatabaseTab = 'overview';
const DEFAULT_DAYS: KeywordDatabaseFillDays = 30;

function parseTab(raw: string | null): KeywordDatabaseTab {
  return KEYWORD_DATABASE_TABS.find((t) => t === raw) ?? DEFAULT_TAB;
}

function parseDays(raw: string | null): KeywordDatabaseFillDays {
  return KEYWORD_DATABASE_FILL_DAYS.find((d) => String(d) === raw) ?? DEFAULT_DAYS;
}

type ForceRef = MutableRefObject<boolean>;
type SimpleBlock = 'headline' | 'overview' | 'markets' | 'sources' | 'coverage';

/** One block's query; `forceRef` is read at fetch time so Refresh can force exactly one round. */
function useBlock<T>(name: SimpleBlock, forceRef: ForceRef) {
  return useQuery({
    queryKey: ['keyword-database', name],
    queryFn: async () =>
      (await api.stats.keywordDatabase.block<T>(name, { force: forceRef.current })).data,
  });
}

function OverviewPanel({ forceRef }: { forceRef: ForceRef }) {
  const query = useBlock<KeywordDatabaseOverview>('overview', forceRef);
  return (
    <BlockPanel label="Overview" query={query}>
      {(overview) => <OverviewTab overview={overview} />}
    </BlockPanel>
  );
}

function FillPanel({
  forceRef,
  days,
  onDaysChange,
}: {
  forceRef: ForceRef;
  days: KeywordDatabaseFillDays;
  onDaysChange: (days: KeywordDatabaseFillDays) => void;
}) {
  const query = useQuery({
    queryKey: ['keyword-database', 'fill', days],
    queryFn: async () =>
      (await api.stats.keywordDatabase.fill(days, { force: forceRef.current })).data,
  });
  return (
    <BlockPanel label="Fill" query={query}>
      {(fill: KeywordDatabaseFill) => (
        <FillTab fill={fill} days={days} onDaysChange={onDaysChange} />
      )}
    </BlockPanel>
  );
}

function MarketsPanel({ forceRef }: { forceRef: ForceRef }) {
  const query = useBlock<KeywordDatabaseMarkets>('markets', forceRef);
  return (
    <BlockPanel label="Markets" query={query}>
      {(markets) => <MarketsTab markets={markets} />}
    </BlockPanel>
  );
}

function SourcesPanel({ forceRef }: { forceRef: ForceRef }) {
  const query = useBlock<KeywordDatabaseSources>('sources', forceRef);
  return (
    <BlockPanel label="Sources" query={query}>
      {(sources) => <SourcesTab sources={sources} />}
    </BlockPanel>
  );
}

function CoveragePanel({ forceRef }: { forceRef: ForceRef }) {
  const query = useBlock<KeywordDatabaseCoverage>('coverage', forceRef);
  return (
    <BlockPanel label="Coverage" query={query}>
      {(coverage) => <CoverageTab coverage={coverage} />}
    </BlockPanel>
  );
}

export function KeywordDatabasePage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const days = parseDays(searchParams.get('days'));
  const forceRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  // Merge into the current params so changing `tab` never drops `days` and vice versa.
  const patchParams = (patch: Record<string, string>) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) next.set(k, v);
      return next;
    });

  const headlineQ = useBlock<KeywordDatabaseHeadline>('headline', forceRef);

  const refresh = async () => {
    forceRef.current = true;
    setRefreshing(true);
    try {
      await queryClient.refetchQueries({ queryKey: ['keyword-database'], type: 'active' });
    } finally {
      forceRef.current = false;
      setRefreshing(false);
    }
  };

  const headlineBlock = headlineQ.data;
  const planeEmpty = headlineBlock?.state === 'no-counted-rows';

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Keyword Database"
        subtitle="The production keyword corpus: totals, fill rate, storefronts, languages, sources and data coverage."
      >
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </PageHeader>

      {headlineQ.isError ? (
        <div
          data-testid="headline-error"
          role="alert"
          className="mb-6 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Headline failed to load. Refresh retries it; the tabs below load on their own.
        </div>
      ) : !headlineBlock ? (
        <div className="mb-6 p-6 text-sm text-zinc-500">Loading headline…</div>
      ) : headlineBlock.state === 'no-counted-rows' ? (
        <NoCountedRows dataset={headlineBlock.dataset} />
      ) : (
        <HeadlineStrip headline={headlineBlock.body} meta={headlineBlock} />
      )}

      {!planeEmpty && (
        <Tabs.Root value={tab} onValueChange={(v) => patchParams({ tab: v })}>
          <Tabs.List className="mb-4 inline-flex items-center gap-1 rounded-lg bg-zinc-100 dark:bg-zinc-900 p-1">
            {KEYWORD_DATABASE_TABS.map((t) => (
              <Tabs.Trigger key={t} value={t} className={TAB_TRIGGER_CLASSES}>
                {TAB_LABEL[t]}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Tabs.Content value="overview">
            <OverviewPanel forceRef={forceRef} />
          </Tabs.Content>
          <Tabs.Content value="fill">
            <FillPanel
              forceRef={forceRef}
              days={days}
              onDaysChange={(d) => patchParams({ days: String(d) })}
            />
          </Tabs.Content>
          <Tabs.Content value="markets">
            <MarketsPanel forceRef={forceRef} />
          </Tabs.Content>
          <Tabs.Content value="sources">
            <SourcesPanel forceRef={forceRef} />
          </Tabs.Content>
          <Tabs.Content value="coverage">
            <CoveragePanel forceRef={forceRef} />
          </Tabs.Content>
        </Tabs.Root>
      )}
    </div>
  );
}
